import { LEAD_LIMITS, leadLabelSports, type AdminLeadLabel, type AdminLeadLabelSuggestion, type LeadLabelSport } from "@teamna/shared";
import { useState, type FormEvent } from "react";
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  ErrorState,
  Field,
  InlineError,
  PageHeader,
  Panel,
  SelectField,
  TextAreaField,
  showToast,
  type DataTableColumn,
} from "@teamna/ui/components/index.ts";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import {
  useAddLeadSuggestion,
  useCreateLeadLabel,
  useDeleteLeadLabel,
  useDismissLeadSuggestion,
  useLeadLabels,
  useLeadSuggestions,
  useUpdateLeadLabel,
} from "../lib/leads-api.ts";
import { LABEL_SPORT_WORDS, LeadsTabs, scoreText } from "./leads-parts.tsx";

/**
 * The labels, `/leads/labels`: add, change and delete labels, each with an optional score and a
 * "how to spot it" text that Claude reads before a run. Claude's label suggestions sit on top:
 * Add makes the label and puts it on the places Claude saw it on; Dismiss means never again.
 */

const onError = (error: Error) => showToast(copyForError(error).message, { tone: "info" });

/** "" is no score. A whole number from -1000 to 1000 is a score. Anything else is an error. */
function parseScore(text: string): { ok: true; score: number | null } | { ok: false } {
  const value = text.trim();
  if (!value) return { ok: true, score: null };
  if (!/^[+-]?\d+$/.test(value)) return { ok: false };
  const score = Number(value);
  return score >= LEAD_LIMITS.scoreMin && score <= LEAD_LIMITS.scoreMax ? { ok: true, score } : { ok: false };
}

const SCORE_ERROR = `Write a whole number from ${LEAD_LIMITS.scoreMin} to ${LEAD_LIMITS.scoreMax}, or leave it empty for no score.`;

const COLUMNS: DataTableColumn<AdminLeadLabel>[] = [
  {
    id: "name",
    header: "Label",
    sortValue: (row) => row.name,
    cell: (row) => (
      <div className="min-w-[140px]">
        <p className="font-semibold text-ink" dir="auto">
          {row.name}
        </p>
        {row.systemKey ? <p className="text-[12px] text-ink-2">Control Center adds it</p> : null}
      </div>
    ),
  },
  {
    id: "score",
    header: "Score",
    align: "end",
    sortValue: (row) => row.score,
    cell: (row) => (row.score === null ? <span className="text-ink-2">None</span> : <span className="font-semibold tabular-nums">{scoreText(row.score)}</span>),
  },
  { id: "sport", header: "Sport", sortValue: (row) => row.sport, cell: (row) => LABEL_SPORT_WORDS[row.sport] },
  {
    id: "spot",
    header: "How to spot it",
    hideOnPhone: true,
    cell: (row) =>
      row.howToSpot ? (
        <span className="block max-w-[420px] text-[13px] text-ink-2" dir="auto">
          {row.howToSpot}
        </span>
      ) : (
        <span className="text-[13px] text-ink-2">Only a person adds it</span>
      ),
  },
  { id: "count", header: "On leads", align: "end", sortValue: (row) => row.leadCount, cell: (row) => <span className="tabular-nums">{row.leadCount}</span> },
];

export function LeadLabelsScreen() {
  const labels = useLeadLabels();
  const suggestions = useLeadSuggestions();
  const [editing, setEditing] = useState<AdminLeadLabel | "new" | null>(null);
  const [deleting, setDeleting] = useState<AdminLeadLabel | null>(null);
  const remove = useDeleteLeadLabel();

  return (
    <>
      <PageHeader
        title="Leads"
        description="Labels decide the score of a lead. Claude reads each label's text before a run."
        actions={<Button onClick={() => setEditing("new")}>Add label</Button>}
      />
      <LeadsTabs current="labels" />
      <div className="flex flex-col gap-6">
        {suggestions.data && suggestions.data.length > 0 ? (
          <section className="flex flex-col gap-3" aria-label="Label suggestions from Claude">
            <h2 className="text-[16px] font-semibold text-ink">Suggestions from Claude</h2>
            {suggestions.data.map((suggestion) => (
              <SuggestionCard key={suggestion.id} suggestion={suggestion} />
            ))}
          </section>
        ) : null}
        {labels.error ? (
          <ErrorState error={labels.error} onRetry={() => void labels.refetch()} />
        ) : (
          <DataTable
            columns={COLUMNS}
            rows={labels.data ?? []}
            rowKey={(row) => row.id}
            rowLabel={(row) => row.name}
            onRowClick={(row) => setEditing(row)}
            isLoading={labels.isLoading}
            ranked
            countLabel={(count) => `${count} ${count === 1 ? "label" : "labels"}`}
            empty={{ title: "No labels", message: "Add a label to start scoring leads." }}
          />
        )}
      </div>
      <Panel open={editing !== null} onClose={() => setEditing(null)} title={editing === "new" ? "Add a label" : (editing?.name ?? "Label")}>
        {editing !== null ? (
          <LabelForm
            key={editing === "new" ? "new" : editing.id}
            label={editing === "new" ? null : editing}
            onDone={() => setEditing(null)}
            onDelete={editing !== "new" && !editing.systemKey ? () => setDeleting(editing) : undefined}
          />
        ) : null}
      </Panel>
      <ConfirmDialog
        open={deleting !== null}
        title={`Delete "${deleting?.name ?? ""}"?`}
        confirmLabel="Delete label"
        tone="danger"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              showToast(`Label "${deleting.name}" deleted.`);
              setDeleting(null);
              setEditing(null);
            },
            onError,
          })
        }
      >
        <p>
          {deleting?.leadCount
            ? `It is on ${deleting.leadCount} ${deleting.leadCount === 1 ? "lead" : "leads"}. It comes off them, and their scores change at once.`
            : "No lead has it."}
        </p>
      </ConfirmDialog>
    </>
  );
}

function LabelForm({ label, onDone, onDelete }: { label: AdminLeadLabel | null; onDone: () => void; onDelete?: () => void }) {
  const create = useCreateLeadLabel();
  const update = useUpdateLeadLabel();
  const [name, setName] = useState(label?.name ?? "");
  const [score, setScore] = useState(label?.score === null || label?.score === undefined ? "" : String(label.score));
  const [sport, setSport] = useState<LeadLabelSport>(label?.sport ?? "both");
  const [howToSpot, setHowToSpot] = useState(label?.howToSpot ?? "");
  const [tried, setTried] = useState(false);
  const parsed = parseScore(score);
  const nameError = tried && !name.trim() ? "Write a name for the label." : undefined;
  const scoreError = tried && !parsed.ok ? SCORE_ERROR : undefined;
  const pending = create.isPending || update.isPending;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    if (!name.trim() || !parsed.ok) return;
    const fields = { name: name.trim(), score: parsed.score, sport, howToSpot: howToSpot.trim() };
    const done = (words: string) => () => {
      showToast(words);
      onDone();
    };
    if (label) update.mutate({ labelId: label.id, changes: fields }, { onSuccess: done("Label saved. Every lead with it has its new score.") });
    else create.mutate(fields, { onSuccess: done(`Label "${fields.name}" added.`) });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      {create.error ?? update.error ? <InlineError error={(create.error ?? update.error)!} /> : null}
      <Field label="Name" value={name} maxLength={LEAD_LIMITS.labelName} onChange={(event) => setName(event.target.value)} error={nameError} dir="auto" />
      <Field
        label="Score"
        hint="A whole number, for example 30 or -100. Leave it empty for a label with no score."
        inputMode="numeric"
        value={score}
        onChange={(event) => setScore(event.target.value)}
        error={scoreError}
      />
      <SelectField label="Sport" value={sport} onChange={(event) => setSport(event.target.value as LeadLabelSport)} hint="Claude puts it only on leads of this sport.">
        {leadLabelSports.map((value) => (
          <option key={value} value={value}>
            {LABEL_SPORT_WORDS[value]}
          </option>
        ))}
      </SelectField>
      <TextAreaField
        label="How to spot it"
        hint="What Claude looks for before it adds this label. Leave it empty for a label only a person adds."
        rows={3}
        maxLength={LEAD_LIMITS.howToSpot}
        value={howToSpot}
        onChange={(event) => setHowToSpot(event.target.value)}
        dir="auto"
      />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" loading={pending}>
          {label ? "Save label" : "Add label"}
        </Button>
        {onDelete ? (
          <Button type="button" variant="danger" onClick={onDelete}>
            Delete label
          </Button>
        ) : null}
      </div>
    </form>
  );
}

function SuggestionCard({ suggestion }: { suggestion: AdminLeadLabelSuggestion }) {
  const add = useAddLeadSuggestion();
  const dismiss = useDismissLeadSuggestion();
  const [score, setScore] = useState(suggestion.score === null ? "" : String(suggestion.score));
  const parsed = parseScore(score);
  return (
    <Card className="flex flex-col gap-3 p-4">
      <div>
        <p className="text-[15px] font-semibold text-ink" dir="auto">
          {suggestion.name}
          <span className="ml-2 text-[13px] font-normal text-ink-2">{LABEL_SPORT_WORDS[suggestion.sport]}</span>
        </p>
        <p className="text-[13px] text-ink-2" dir="auto">
          {suggestion.howToSpot}
        </p>
        <p className="mt-1 text-[13px] text-ink-2">
          Seen on {suggestion.leads.length} {suggestion.leads.length === 1 ? "place" : "places"}
          {suggestion.run ? ` in run #${suggestion.run.number}` : ""}
          {suggestion.leads.length ? `: ${suggestion.leads.map((lead) => lead.name).join(", ")}` : ""}.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-[160px]">
          <Field label="Score" size="compact" inputMode="numeric" value={score} onChange={(event) => setScore(event.target.value)} error={parsed.ok ? undefined : SCORE_ERROR} />
        </div>
        <Button
          size="sm"
          loading={add.isPending}
          disabled={!parsed.ok}
          onClick={() =>
            parsed.ok &&
            add.mutate(
              { suggestionId: suggestion.id, changes: { score: parsed.score } },
              { onSuccess: (label) => showToast(`Label "${label.name}" added to ${label.leadCount} ${label.leadCount === 1 ? "lead" : "leads"}.`), onError },
            )
          }
        >
          Add label
        </Button>
        <Button size="sm" variant="secondary" loading={dismiss.isPending} onClick={() => dismiss.mutate(suggestion.id, { onSuccess: () => showToast("Suggestion dismissed."), onError })}>
          Dismiss
        </Button>
      </div>
    </Card>
  );
}
